export interface AuthenticatedFetchOptions {
  getAccessToken: () => string | null;
  refresh: () => Promise<void>;
  onExpired: () => void;
  fetcher?: typeof fetch;
}

// Only confirmed invalid credentials end the session. A timeout or temporary
// Cognito/network failure must leave the tokens available for the next retry.
export class SessionExpiredError extends Error {
  constructor(message = "Your session expired. Sign in again.") {
    super(message);
    this.name = "SessionExpiredError";
  }
}

// Build one authenticated request function for the whole app. Its refresh
// promise is shared, so a burst of expired writes produces one Cognito renewal
// rather than one renewal per request.
export function createAuthenticatedFetch(options: AuthenticatedFetchOptions) {
  let refreshInFlight: Promise<void> | null = null;

  const withAuth = (headers?: HeadersInit): Headers => {
    const next = new Headers(headers);
    const token = options.getAccessToken();
    if (token) next.set("authorization", "Bearer " + token);
    return next;
  };

  const expire = (): never => {
    options.onExpired();
    throw new SessionExpiredError();
  };

  return async (url: string, init: RequestInit = {}): Promise<Response> => {
    const fetcher = options.fetcher ?? fetch;
    init.signal?.throwIfAborted();
    let response = await fetcher(url, { ...init, headers: withAuth(init.headers) });
    init.signal?.throwIfAborted();
    if (response.status !== 401) return response;

    try {
      if (!refreshInFlight) {
        refreshInFlight = options.refresh().finally(() => { refreshInFlight = null; });
      }
      await refreshInFlight;
      init.signal?.throwIfAborted();
    } catch (error) {
      init.signal?.throwIfAborted();
      if (error instanceof SessionExpiredError) expire();
      throw error;
    }

    response = await fetcher(url, { ...init, headers: withAuth(init.headers) });
    init.signal?.throwIfAborted();
    if (response.status === 401) expire();
    return response;
  };
}
